/**
 * Achievement awarding — projected from history (auto).
 */

import { ACHIEVEMENT_DEFINITIONS } from "./rankingTypes";
import { calculateCareerStatistics, projectTournamentHistory } from "../player/playerCareer";

/**
 * Award achievements from cards + optional placements.
 * Mirrors catalog in ACHIEVEMENT_DEFINITIONS.
 *
 * @param {object} input
 * @param {Array<object>} input.cards
 * @param {Array<{ placement: string }>} [input.placements]
 * @param {number | null} [input.seasonYear]
 */
export function awardAchievements(input = {}) {
  const cards = input.cards || [];
  const seasonYear = input.seasonYear ?? null;
  const scoped =
    seasonYear == null
      ? cards
      : cards.filter((c) => new Date(c.completedAt || 0).getFullYear() === seasonYear);
  const stats = calculateCareerStatistics(scoped);
  const placements = input.placements || [];
  const titles = placements.filter((p) => p.placement === "winner").length + (stats.titles || 0);
  const finals =
    placements.filter((p) => p.placement === "winner" || p.placement === "runner_up").length +
    (stats.runnerUps || 0) +
    (stats.titles || 0);

  /** @type {Set<string>} */
  const earned = new Set();

  if (stats.competitionsPlayed >= 1 || placements.some((p) => p.placement === "participant")) {
    earned.add("first_tournament");
  }
  if (stats.wins >= 1) earned.add("first_match_win");
  if (titles >= 1) earned.add("champion");
  if (stats.wins >= 10) earned.add("wins_10");
  if (stats.wins >= 50) earned.add("wins_50");
  if (stats.wins >= 100) earned.add("wins_100");
  if (stats.longestWinningStreak >= 5 || stats.currentWinningStreak >= 5) {
    earned.add("winning_streak_5");
  }
  if (finals >= 1) earned.add("finalist");
  if (scoped.length >= 20 && seasonYear != null) earned.add("most_active");
  if (cards.length >= 20 && seasonYear == null) {
    // lifetime most-active threshold also via career volume — optional
  }

  const history = projectTournamentHistory(scoped);
  history.forEach((t) => {
    if (t.matches >= 3 && t.wins === t.matches && t.losses === 0) {
      earned.add("perfect_tournament");
    }
  });

  return ACHIEVEMENT_DEFINITIONS.filter((d) => earned.has(d.id)).map((d) => ({
    ...d,
    awarded: true,
    awardedAtMs: Date.now(),
  }));
}

/**
 * Recent form string last N matches (W/L).
 * @param {Array<{ isWin?: boolean }>} cards newest-first
 * @param {number} [n]
 */
export function projectRecentForm(cards, n = 10) {
  const slice = (cards || []).slice(0, n);
  return {
    results: slice.map((c) => (c.isWin ? "W" : "L")),
    wins: slice.filter((c) => c.isWin).length,
    losses: slice.filter((c) => !c.isWin).length,
    label: slice.map((c) => (c.isWin ? "W" : "L")).join("") || "—",
  };
}
